/**
 * CeremonyShell — the frame both signing ceremonies live inside.
 *
 * A ceremony is a full-screen, single-purpose flow: no app chrome, no section drawer, no
 * way to wander off mid-signature. What it does keep is a close control, the document's
 * name, and a progress rail — the three things that answer "where am I, and can I stop?".
 *
 * The layout is a column: fixed head, scrolling body, fixed foot. The primary action lives
 * in the foot on every step, which is where a thumb is, and it is the ONLY primary action
 * on screen at any moment.
 */

import type { ReactNode } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Props {
    title: string;
    /** Zero-based index into `steps`. Ignored where there are no steps. */
    stepIndex?: number;
    /**
     * The progress rail.
     *
     * OPTIONAL, because the at-rest viewer is not a ceremony: it has one screen, nothing to
     * progress through, and a rail drawn over it would claim a sequence that does not exist.
     * One shell still, because everything else about the frame — the close control, the
     * centred title, the sticky foot — is exactly what a driver reading their executed
     * agreement wants, and a second shell would be a second thing to keep in step.
     */
    steps?: readonly string[];
    /**
     * What sits in the rail's place when there is no rail: the viewer's
     * `Agreement | Terms vN` control. Never both — a header carries one navigational idea.
     */
    tabs?: ReactNode;
    onClose?: () => void;
    closeLabel?: string;
    children: ReactNode;
    /** Footer: the primary action and its note. */
    footer?: ReactNode;
}

export default function CeremonyShell({
    title,
    stepIndex = 0,
    steps,
    tabs,
    onClose,
    closeLabel = 'Close',
    children,
    footer,
}: Props) {
    return (
        <div className="flex min-h-[100dvh] flex-col bg-background">
            <header className="sticky top-0 z-20 flex-none border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70">
                <div className="mx-auto flex w-full max-w-2xl items-center gap-3 px-4 py-2.5">
                    {onClose ? (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            aria-label={closeLabel}
                            onClick={onClose}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    ) : (
                        <span className="h-8 w-8 shrink-0" aria-hidden="true" />
                    )}
                    <h1 className="flex-1 truncate text-center text-[13px] font-semibold tracking-tight">
                        {title}
                    </h1>
                    <span className="h-8 w-8 shrink-0" aria-hidden="true" />
                </div>
                {steps && steps.length > 0 ? (
                    <div
                        className="mx-auto flex w-full max-w-2xl gap-1 px-4 pb-2.5"
                        aria-hidden="true"
                    >
                        {steps.map((step, index) => (
                            <span
                                key={step}
                                className={cn(
                                    'h-[3px] flex-1 rounded-full transition-colors',
                                    index <= stepIndex ? 'bg-foreground' : 'bg-border',
                                )}
                            />
                        ))}
                    </div>
                ) : null}

                {!steps && tabs ? (
                    <div className="mx-auto w-full max-w-2xl px-4 pb-2.5">{tabs}</div>
                ) : null}
            </header>

            <main className="mx-auto w-full max-w-2xl flex-1 px-4 pb-8 pt-4">{children}</main>

            {footer && (
                <footer className="sticky bottom-0 z-20 flex-none border-t border-border bg-background/95 px-4 pb-6 pt-3 backdrop-blur supports-[backdrop-filter]:bg-background/70">
                    <div className="mx-auto w-full max-w-2xl space-y-2">{footer}</div>
                </footer>
            )}
        </div>
    );
}

/** The one-line note under a ceremony's primary action. */
export function CeremonyNote({ children }: { children: ReactNode }) {
    return <p className="text-center text-[11.5px] text-muted-foreground">{children}</p>;
}
